/**
 * The Leads working list's filters, sort and page. The filters use the API's own parameter
 * names (GET /api/v1/leads), so a filtered list can be bookmarked and the same string drives
 * the page, the export and the address bar. The sort is a column and a direction (`order`,
 * `dir`); the API sorts when it can do that exact sort, and the page sorts itself otherwise.
 */
import type { LeadSort, NextTouchFilter, StageFilter } from "@/api/models";

export const PAGE_SIZE = 50;

const STAGES: readonly StageFilter[] = [
  "waiting_for_court", "executor_approved", "heirs_can_sign", "order_returned_unsigned", "closed", "not_stated", "dropped",
];
const NEXT: readonly NextTouchFilter[] = ["all", "due", "week"];

export type LeadColumn = "petitioner" | "phone" | "deceased" | "property" | "house" | "value" | "filed" | "next_step" | "contact";
export const LEAD_COLUMNS: readonly LeadColumn[] = [
  "petitioner", "phone", "deceased", "property", "house", "value", "filed", "next_step", "contact",
];
export type SortDir = "asc" | "desc";

export interface LeadsQuery {
  q: string;
  /** One county, such as "Harris, TX"; empty for every county the workspace works. */
  county: string;
  stage: StageFilter | "";
  next_touch: NextTouchFilter;
  has_phone: boolean;
  has_property: boolean;
  /** A named petitioner or reported representative; a court-listed name never counts. */
  has_person: boolean;
  order: LeadColumn;
  dir: SortDir;
  page: number;
}

/** Most urgent first: the next step by the day it falls due. */
export const DEFAULT_QUERY: LeadsQuery = {
  q: "",
  county: "",
  stage: "",
  next_touch: "all",
  has_phone: false,
  has_property: false,
  has_person: false,
  order: "next_step",
  dir: "asc",
  page: 1,
};

/** The API sort for a column and direction, or null when only the page can sort it. */
export function serverSort(order: LeadColumn, dir: SortDir): LeadSort | null {
  if (order === "next_step" && dir === "asc") return "next_touch";
  if (order === "petitioner" && dir === "asc") return "petitioner";
  if (order === "value" && dir === "desc") return "highest_value";
  if (order === "filed") return dir === "desc" ? "newest_filing" : "oldest_filing";
  return null;
}

/** An address bar from before the sortable headers used the API's sort names. */
const LEGACY_SORT: Record<LeadSort, [LeadColumn, SortDir]> = {
  next_touch: ["next_step", "asc"],
  petitioner: ["petitioner", "asc"],
  highest_value: ["value", "desc"],
  newest_filing: ["filed", "desc"],
  oldest_filing: ["filed", "asc"],
};

function oneOf<T extends string>(value: string | null, allowed: readonly T[], fallback: T): T {
  return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

export function queryFromSearch(params: URLSearchParams): LeadsQuery {
  const page = Number.parseInt(params.get("page") ?? "1", 10);
  const legacy = LEGACY_SORT[params.get("sort") as LeadSort] as [LeadColumn, SortDir] | undefined;
  return {
    q: (params.get("q") ?? "").trim().slice(0, 200),
    county: (params.get("county") ?? "").trim().slice(0, 80),
    stage: oneOf<StageFilter | "">(params.get("stage"), STAGES, ""),
    next_touch: oneOf(params.get("next_touch"), NEXT, "all"),
    has_phone: params.get("has_phone") === "true",
    has_property: params.get("has_property") === "true",
    has_person: params.get("has_person") === "true",
    order: oneOf(params.get("order"), LEAD_COLUMNS, legacy?.[0] ?? "next_step"),
    dir: params.get("dir") === "desc" || (!params.get("dir") && legacy?.[1] === "desc") ? "desc" : "asc",
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
}

/** The API's `sort` from a request, for the mock: what GET /leads was asked. */
export function apiSortFromSearch(params: URLSearchParams): LeadSort {
  const sort = params.get("sort");
  return sort && sort in LEGACY_SORT ? (sort as LeadSort) : "next_touch";
}

/** The filters only, defaults left out: what the export and the page share. */
function filterParams(query: LeadsQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  if (query.county) params.set("county", query.county);
  if (query.stage) params.set("stage", query.stage);
  if (query.next_touch !== "all") params.set("next_touch", query.next_touch);
  if (query.has_phone) params.set("has_phone", "true");
  if (query.has_property) params.set("has_property", "true");
  if (query.has_person) params.set("has_person", "true");
  return params;
}

/** The browser URL: a clean list has a clean address. */
export function searchFromQuery(query: LeadsQuery): URLSearchParams {
  const params = filterParams(query);
  if (query.order !== DEFAULT_QUERY.order || query.dir !== DEFAULT_QUERY.dir) {
    params.set("order", query.order);
    params.set("dir", query.dir);
  }
  if (query.page > 1) params.set("page", String(query.page));
  return params;
}

/** GET /api/v1/leads parameters: the API's sort when it can do this one, most urgent first otherwise. */
export function apiParams(query: LeadsQuery): URLSearchParams {
  const params = filterParams(query);
  const sort = serverSort(query.order, query.dir) ?? "next_touch";
  if (sort !== "next_touch") params.set("sort", sort);
  params.set("page", String(query.page));
  params.set("page_size", String(PAGE_SIZE));
  return params;
}

/** GET /api/v1/leads/export.csv parameters: the current list, every page, in the API's order. */
export function exportParams(query: LeadsQuery): URLSearchParams {
  const params = filterParams(query);
  const sort = serverSort(query.order, query.dir);
  if (sort && sort !== "next_touch") params.set("sort", sort);
  return params;
}

/** Changing a filter or the sort goes back to page 1. */
export function withFilter(query: LeadsQuery, change: Partial<Omit<LeadsQuery, "page">>): LeadsQuery {
  return { ...query, ...change, page: 1 };
}

export function isFiltered(query: LeadsQuery): boolean {
  return Boolean(query.q || query.county || query.stage || query.next_touch !== "all" || query.has_phone || query.has_property || query.has_person);
}

export function clearFilters(query: LeadsQuery): LeadsQuery {
  return { ...DEFAULT_QUERY, order: query.order, dir: query.dir };
}
